import { shiftDay, type Locale } from '@iac/contracts';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { LineChart } from './LineChart';
import { Panel } from './Panel';

const sample: Dictionary<{
  label: string;
  limit: string;
  weight: string;
  target: string;
}> = {
  pl: {
    label: 'Zjedzone kalorie',
    limit: 'Limit',
    weight: 'Waga',
    target: 'Cel',
  },
  en: {
    label: 'Calories eaten',
    limit: 'Limit',
    weight: 'Weight',
    target: 'Target',
  },
};

/** A fortnight of kcal; day 6 was never logged. */
const KCAL = [
  1640,
  1820,
  1510,
  2240,
  1760,
  null,
  1930,
  1580,
  1700,
  2100,
  1450,
  1880,
  1620,
  1790,
];

const fortnight = KCAL.map((value, i) => ({
  date: shiftDay('2026-10-01', i),
  value,
}));

/** Two fortnights back to back, the second with its own gap, ending on the 30th. */
const month = [...KCAL, 1710, 1560, ...KCAL].map((value, i) => ({
  date: shiftDay('2026-10-01', i),
  value: i === 21 ? null : value,
}));

/** Weighed every few days, unevenly — only the measured days are passed. */
const weights = [
  [0, 84.1],
  [2, 83.8],
  [3, 84.0],
  [7, 83.2],
  [9, 83.4],
  [14, 82.7],
  [16, 82.9],
  [17, 82.3],
  [23, 81.9],
  [27, 81.6],
].map(([offset, value]) => ({ date: shiftDay('2026-10-01', offset), value }));

function Chart({
  locale,
  points,
  limit,
}: {
  locale: Locale;
  points: { date: string; value: number | null }[];
  limit?: boolean;
}) {
  const t = sample[locale];
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    timeZone: 'UTC',
  });
  const number = new Intl.NumberFormat(locale);
  return (
    <Panel as="div">
      <LineChart
        label={t.label}
        points={points}
        reference={limit ? { value: 2000, label: t.limit } : undefined}
        formatDate={(date) => day.format(new Date(`${date}T00:00:00Z`))}
        formatValue={(value) => number.format(value)}
      />
    </Panel>
  );
}

function WeightChart({ locale }: { locale: Locale }) {
  const t = sample[locale];
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    timeZone: 'UTC',
  });
  const kg = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return (
    <Panel as="div">
      <LineChart
        label={t.weight}
        points={weights}
        reference={{ value: 80, label: t.target }}
        axis="fit"
        formatDate={(date) => day.format(new Date(`${date}T00:00:00Z`))}
        formatValue={(value) => kg.format(value)}
      />
    </Panel>
  );
}

export default {
  kind: 'component',
  title: { pl: 'Wykres liniowy', en: 'Line chart' },
  description: {
    pl: 'Jedna wartość na dzień jako linia. Podaj każdy dzień zakresu — `value: null` przerywa linię zamiast spadać do zera. Oś pionowa zaczyna się od 0 — albo, z `axis="fit"`, obejmuje tylko zakres wartości z marginesem — i zawsze sięga `reference`, rysowanej jako przerywana linia z etykietą. Daty, które nie mieszczą się poziomo, idą ukośnie pod kątem 45°; gdy i to za ciasno, opisany jest co n-ty dzień, licząc od ostatniego. Najechanie lub dotknięcie pokazuje datę i wartość najbliższego dnia. Formatowanie dat i liczb należy do wołającego. Czytnik ekranu dostaje nazwę z `label` i listę wartości.',
    en: 'One value per day, as a line. Pass every day of the range — `value: null` breaks the line instead of dropping to zero. The vertical axis starts at 0 — or, with `axis="fit"`, spans just the values with some padding — and always reaches `reference`, drawn as a dashed, labelled line. Dates that do not fit level slant at 45°; if even that is too tight, every n-th day is labelled, counted back from the last. Hovering or touching shows the nearest day’s date and value. Formatting dates and numbers is the caller’s. A screen reader gets the name from `label` and a list of the values.',
  },
  variants: [
    {
      name: { pl: 'Dwa tygodnie z limitem', en: 'Fortnight with a limit' },
      render: (locale) => <Chart locale={locale} points={fortnight} limit />,
    },
    {
      name: { pl: 'Miesiąc', en: 'A month' },
      render: (locale) => <Chart locale={locale} points={month} limit />,
    },
    {
      name: {
        pl: 'Oś dopasowana, rzadkie pomiary',
        en: 'Fitted axis, sparse measurements',
      },
      render: (locale) => <WeightChart locale={locale} />,
    },
    {
      name: { pl: 'Jeden dzień', en: 'A single day' },
      render: (locale) => (
        <Chart locale={locale} points={[{ date: '2026-10-01', value: 1640 }]} />
      ),
    },
    {
      name: { pl: 'Bez danych', en: 'No data' },
      render: (locale) => (
        <Chart
          locale={locale}
          points={fortnight.map(({ date }) => ({ date, value: null }))}
        />
      ),
    },
  ],
} satisfies Story;
